export const LOR_REVIEW_TAG = '[[LOR_REVIEW_TABLE]]';
export const LOR_UPLOAD_REVIEW_TAG = '[[LOR_UPLOAD_REVIEW]]';

export type LorReviewRow = {
  field?: string;
  provided_value?: string;
  providedValue?: string;
  authoritative_value?: string;
  authoritativeValue?: string;
  status?: string;
  notes?: string;
  citations?: string[] | string;
};

export type ParsedLorReview = {
  rows: LorReviewRow[];
  remainingContent: string;
};

export type NormalizedCitation =
  | { kind: 'modern'; id: string }
  | { kind: 'legacy'; artifactId: string; citationIds: string }
  | { kind: 'text'; label: string };

export type NormalizedLorReviewRow = {
  id: string;
  field: string;
  provided: string;
  authoritative: string;
  status: string;
  notes: string;
  citations: NormalizedCitation[];
};

export const CITE_TAG_REGEX = /<cite\b[^>]*>/gi;
export const ATTR_REGEX = (name: string) =>
  new RegExp(`${name}=(["'])((?:\\\\\\1|.)*?)\\1`, 'i');

export const cleanCitationAttr = (value: string | undefined): string | null => {
  if (!value) return null;
  // Unescape quotes
  return value.replace(/\\"/g, '"').replace(/\\'/g, "'").trim();
};

export const normalizeCitationStrings = (
  raw: string[],
): NormalizedCitation[] => {
  const normalized: NormalizedCitation[] = [];

  raw.forEach((entry) => {
    let str = entry.trim();
    if (!str) return;

    // Clean closing tags to prevent them appearing as text
    str = str.replace(/<\/cite>/gi, '');

    CITE_TAG_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null;
    let lastIndex = 0;

    // eslint-disable-next-line no-cond-assign
    while ((match = CITE_TAG_REGEX.exec(str)) !== null) {
      const tagContent = match[0];

      // Extract attributes from the tag string
      const artifactIdMatch = tagContent.match(ATTR_REGEX('artifact-id'));
      const citationIdsMatch = tagContent.match(ATTR_REGEX('citation-ids'));
      const citationIdMatch = tagContent.match(ATTR_REGEX('citation-id'));

      const artifactId = cleanCitationAttr(artifactIdMatch?.[2]);
      const citationIds = cleanCitationAttr(citationIdsMatch?.[2]);
      const citationIdOnly = cleanCitationAttr(citationIdMatch?.[2]);

      if (citationIdOnly) {
        normalized.push({ kind: 'modern', id: citationIdOnly });
      } else if (artifactId && citationIds) {
        normalized.push({ kind: 'legacy', artifactId, citationIds });
      }

      lastIndex = CITE_TAG_REGEX.lastIndex;
    }

    const remainder = str.slice(lastIndex).trim();
    if (remainder) {
      remainder
        .split(/[,\n]+/)
        .map((t) => t.trim())
        .filter(
          (t) =>
            t &&
            t !== '</cite>' &&
            t !== '/>' &&
            !t.startsWith('<cite') &&
            !t.endsWith('>'),
        )
        .forEach((token) => {
          normalized.push({ kind: 'modern', id: token });
        });
    }
  });

  return normalized;
};

export const parseLorReviewTable = (
  content: string | undefined,
): ParsedLorReview | null => {
  if (!content || typeof content !== 'string') {
    return null;
  }

  // Try to find the tag (handle potential missing brackets or malformed tags)
  // We look for LOR_REVIEW_TABLE surrounded by brackets
  const tagMatch = content.match(/\[*LOR_REVIEW_TABLE\]*/);
  if (!tagMatch) return null;

  const tagIndex = tagMatch.index!;
  const afterTag = content.slice(tagIndex + tagMatch[0].length);

  let jsonCandidate = afterTag.trim();

  // If wrapped in markdown code block, extract inner content
  const codeBlockMatch = jsonCandidate.match(/```(?:json)?([\s\S]*?)```/);
  if (codeBlockMatch) {
    jsonCandidate = codeBlockMatch[1].trim();
  }

  // Find JSON array start and end
  const firstBracket = jsonCandidate.indexOf('[');
  const lastBracket = jsonCandidate.lastIndexOf(']');

  if (
    firstBracket === -1 ||
    lastBracket === -1 ||
    lastBracket <= firstBracket
  ) {
    return null;
  }

  const jsonString = jsonCandidate.slice(firstBracket, lastBracket + 1);

  // Helper to try parsing
  const tryParse = (str: string): LorReviewRow[] | null => {
    try {
      const rows = JSON.parse(str);
      return Array.isArray(rows) ? rows : null;
    } catch {
      return null;
    }
  };

  let rows = tryParse(jsonString);

  // Fallback 1: Sanitize invalid backslashes before non-special chars (e.g. \' -> ')
  if (!rows) {
    const sanitized = jsonString.replace(/\\([^"\\/bfnrtu])/g, '$1');
    rows = tryParse(sanitized);
  }

  // Fallback 2: Strip citations if they are causing the parse error
  if (!rows) {
    const noCitations = jsonString.replace(
      /"citations"\s*:\s*\[[\s\S]*?\]\s*(?=[,}])/g,
      '"citations": []',
    );
    rows = tryParse(noCitations);
  }

  if (rows) {
    const cleaned = content.slice(0, tagIndex).trim();
    return {
      rows,
      remainingContent: cleaned,
    };
  }

  return null;
};

export const STATUS_STYLE_MAP: Record<string, string> = {
  MATCH: 'bg-green-100 text-green-800 border-green-200',
  EDIT: 'bg-amber-100 text-amber-800 border-amber-200',
  MISSING: 'bg-red-100 text-red-800 border-red-200',
  VERIFY: 'bg-blue-100 text-blue-800 border-blue-200',
  DEFAULT: 'bg-gray-100 text-gray-700 border-gray-200',
};

const normalizeValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  const str = String(value).trim();
  return str.length ? str : '—';
};

export const normalizeLorReviewRow = (
  row: unknown,
  index: number,
): NormalizedLorReviewRow | null => {
  if (!row || typeof row !== 'object') return null;
  const data = row as Record<string, unknown>;

  const getVal = (keys: string[]) => {
    const foundKey = keys.find((k) => k in data && data[k] != null);
    return foundKey ? normalizeValue(data[foundKey]) : null;
  };

  const field = getVal(['field', 'mapping', 'name']) ?? '—';
  const provided = getVal(['provided_value', 'providedValue']) ?? '—';
  const authoritative =
    getVal(['authoritative_value', 'authoritativeValue']) ?? '—';
  const status = (getVal(['status']) ?? 'MATCH').toUpperCase();
  const notes = getVal(['notes']) ?? '—';

  const rawCitations = data.citations;
  const citationsValues = Array.isArray(rawCitations)
    ? rawCitations.map(String)
    : typeof rawCitations === 'string'
      ? [rawCitations]
      : [];

  const citations = normalizeCitationStrings(citationsValues);

  return {
    id: `${field}-${index}`,
    field,
    provided,
    authoritative,
    status,
    notes,
    citations,
  };
};

/**
 * Review status for a single field from FinchGPT LOR review.
 */
export interface FieldReviewStatus {
  /** Status: MATCH, EDIT, MISSING, VERIFY */
  status: string;
  /** FinchGPT's notes/complaint about the field */
  notes: string;
  /** Whether this field is flagged as problematic (EDIT, MISSING) */
  isProblematic: boolean;
}

/**
 * Map of field names to their review status from FinchGPT.
 */
export type FieldReviewMap = Record<string, FieldReviewStatus>;

/**
 * Known field name variations to normalize for matching.
 * Maps display names to canonical DocumentFieldMapping keys.
 */
const FIELD_NAME_ALIASES: Record<string, string[]> = {
  CLAIM_NUMBER: ['claim_number', 'claim number', 'claimnumber'],
  POLICY_NUMBER: ['policy_number', 'policy number', 'policynumber'],
  CARRIER_NAME: ['carrier_name', 'carrier name', 'carriername', 'carrier'],
  CARRIER_ADDRESS_1: [
    'carrier_address_1',
    'carrier address',
    'carrier_address',
    'carrieraddress',
  ],
  ADJUSTER_NAME: ['adjuster_name', 'adjuster name', 'adjustername', 'adjuster'],
  ADJUSTER_CONTACT: [
    'adjuster_contact',
    'adjuster contact',
    'adjustercontact',
  ],
  CONTACT_METHOD: ['contact_method', 'contact method', 'contactmethod'],
  DATE_OF_LOSS: ['date_of_loss', 'date of loss', 'dateofloss', 'dol'],
  CLIENT_NAME: ['client_name', 'client name', 'clientname', 'client'],
  CLIENT_ADDRESS_1: [
    'client_address_1',
    'client address',
    'client_address',
    'clientaddress',
  ],
  CLIENT_DATE_OF_BIRTH: [
    'client_date_of_birth',
    'client dob',
    'dob',
    'date of birth',
  ],
};

/**
 * Normalize a field name to match against DocumentFieldMapping keys.
 */
const normalizeFieldName = (fieldName: string): string => {
  const lower = fieldName.toLowerCase().trim();

  // Check against known aliases
  const match = Object.entries(FIELD_NAME_ALIASES).find(
    ([canonical, aliases]) =>
      aliases.includes(lower) || lower === canonical.toLowerCase(),
  );

  if (match) {
    return match[0];
  }

  // Fallback: uppercase with underscores
  return lower.replace(/\s+/g, '_').toUpperCase();
};

/**
 * Extracts a map of field review statuses from LOR review rows.
 * This allows the fields panel to show problematic fields with red highlighting
 * and display FinchGPT's notes as tooltips.
 */
export const extractFieldReviewMap = (
  rows: LorReviewRow[],
): FieldReviewMap => {
  const map: FieldReviewMap = {};

  rows.forEach((rawRow, index) => {
    const row = normalizeLorReviewRow(rawRow, index);
    if (!row || row.field === '—') return;

    const normalizedKey = normalizeFieldName(row.field);
    const isProblematic =
      row.status === 'EDIT' || row.status === 'MISSING' || row.status === 'VERIFY';

    map[normalizedKey] = {
      status: row.status,
      notes: row.notes !== '—' ? row.notes : '',
      isProblematic,
    };

    // Also store by the original field name for direct matches
    const originalKey = row.field.replace(/\s+/g, '_').toUpperCase();
    if (originalKey !== normalizedKey) {
      map[originalKey] = map[normalizedKey];
    }
  });

  return map;
};

/**
 * Extracts the most recent LOR review data from a list of chat messages.
 * Scans messages for LOR_REVIEW_TABLE content and returns the field review map.
 */
export const extractFieldReviewMapFromMessages = (
  messages: Array<{ message: { type: string; content: unknown } }>,
): FieldReviewMap => {
  // Scan messages in reverse to find the most recent LOR review
  const aiMessages = [...messages]
    .reverse()
    .filter(
      (msg): msg is { message: { type: 'ai'; content: string } } =>
        msg.message.type === 'ai' && typeof msg.message.content === 'string',
    );

  const matchingMessage = aiMessages.find((msg) => {
    const parsed = parseLorReviewTable(msg.message.content);
    return parsed && parsed.rows.length > 0;
  });

  if (matchingMessage) {
    const parsed = parseLorReviewTable(matchingMessage.message.content);
    if (parsed) {
      return extractFieldReviewMap(parsed.rows);
    }
  }

  return {};
};
